// The dig algorithm (SPEC F6), as pure functions. Randomness and the clock
// are passed in, so every rule is testable.

/** A channel as the app sees it: public/crate/channels.json plus local edits. */
export interface CrateChannel {
  id: string;
  name: string;
  styles: string[];
  genres: string[];
  weight: 1 | 2 | 3 | 4;
  /** Default `mixed` (a registry written before v1.1 has none) */
  era?: 'reissue' | 'new' | 'mixed';
  enabled: boolean;
}

export type ShardRecord = [videoId: string, title: string, chIdx: number, year: number, durS: number, kw: string[], session?: 1];

/** public/crate/<styleId>.json (SPEC F6 crate build, step 7) */
export interface CrateShard {
  /** v2 adds the optional 7th record field (session title); v1 is read as it is until the next refresh replaces it */
  v: 1 | 2;
  builtAt: string;
  /** The oldest record fetch date (build side); absent on shards built before the 30-day rule */
  fetchedAt?: string;
  channels: [id: string, name: string][];
  records: ShardRecord[];
}

export const DAY_MS = 86_400_000;

/** YouTube Developer Policies III.E.4.d: data from public channels is deleted or refreshed within 30 days. */
export const CRATE_MAX_AGE_DAYS = 30;

/** True when the shard's data is more than 30 days old (by its oldest fetch, else its build date). */
export function crateExpired(shard: Pick<CrateShard, 'builtAt' | 'fetchedAt'>, now: number): boolean {
  const from = Date.parse(shard.fetchedAt ?? shard.builtAt);
  return Number.isNaN(from) || now - from > CRATE_MAX_AGE_DAYS * DAY_MS;
}

export interface Candidate {
  videoId: string;
  title: string;
  channelId: string;
  channelName: string;
  year: number;
  durS: number;
  kw: string[];
  /** Channel weight (1, 2, 4) times the keyword boost (x2) */
  weight: number;
  /** The style's keywords found in this record */
  matched: string[];
  /** A session title (SESSION_TITLE): hidden unless Include new recordings is on */
  session?: boolean;
  /** Genre mode: the genre whose shard it came from (Why line) */
  genre?: string;
}

export interface PoolOptions {
  /** videoId to the time it was last heard (ms) */
  heardAt: Record<string, number>;
  bad: ReadonlySet<string>;
  /** Marked Not a record: excluded like `bad` */
  notRecord?: { has(videoId: string): boolean };
  rediscover: boolean;
  now: number;
  /** Include new recordings: `new` channels and `session` records are eligible (default off) */
  includeNew?: boolean;
  /** Genre mode: the shard's genre, for the Why line */
  genre?: string;
}

export const EXCLUDE_DAYS = 90;
/** SPEC F6: channel weight 1, 2, 3, 4 counts as 1, 2, 4, 8 in the pool. */
export const CHANNEL_WEIGHT = { 1: 1, 2: 2, 3: 4, 4: 8 } as const;
export const KEYWORD_BOOST = 2;

/** The style's keywords that this record's keywords contain (a phrase matches its bigram). */
export function matchedKeywords(recordKw: readonly string[], styleKw: readonly string[]): string[] {
  const have = new Set(recordKw);
  return styleKw.filter((k) => have.has(k));
}

/**
 * 1. Pool: records from enabled channels tagged with the style (Genres mode: `style` is undefined, the shard is the
 *    genre's, and the style has no effect), weighted by channel weight.
 * 2. Boost: x2 once if any of the style's keywords match.
 * 3. Exclude: records from `new` channels and `session` records (unless Include new recordings), heard in the last
 *    90 days (unless Rediscover), and records marked bad or Not a record.
 */
export function buildPool(
  shard: CrateShard,
  channels: readonly CrateChannel[],
  style: { id: string; crateKeywords: readonly string[] } | undefined,
  opts: PoolOptions,
): Candidate[] {
  const byId = new Map(channels.map((c) => [c.id, c]));
  const pool: Candidate[] = [];
  for (const record of shard.records) {
    const videoId = record[0];
    const candidate = toCandidate(shard, record, byId, style?.crateKeywords ?? []);
    const channel = candidate && byId.get(candidate.channelId);
    if (!candidate || !channel || !channel.enabled || (style && !channel.styles.includes(style.id))) continue;
    if (!opts.includeNew && (channel.era === 'new' || candidate.session)) continue;
    if (opts.bad.has(videoId) || opts.notRecord?.has(videoId)) continue;
    if (opts.genre) candidate.genre = opts.genre;
    const heard = opts.heardAt[videoId];
    if (!opts.rediscover && heard !== undefined && opts.now - heard < EXCLUDE_DAYS * DAY_MS) continue;
    pool.push(candidate);
  }
  return pool;
}

/** One shard record as a Candidate, or undefined when its channel isn't in the registry. No eligibility rules here. */
export function toCandidate(
  shard: Pick<CrateShard, 'channels'>,
  record: ShardRecord,
  channelsById: ReadonlyMap<string, CrateChannel>,
  styleKeywords: readonly string[],
): Candidate | undefined {
  const [videoId, title, chIdx, year, durS, kw] = record;
  const ref = shard.channels[chIdx];
  const channel = ref ? channelsById.get(ref[0]) : undefined;
  if (!channel) return undefined;
  const matched = matchedKeywords(kw, styleKeywords);
  return {
    videoId, title, channelId: channel.id, channelName: channel.name, year, durS, kw,
    weight: CHANNEL_WEIGHT[channel.weight] * (matched.length > 0 ? KEYWORD_BOOST : 1),
    matched,
    ...(record[6] === 1 ? { session: true } : {}),
  };
}

/**
 * Reopening a saved mark or a history row (SPEC F6): the record as it is in any shard, the current
 * style's first. The user asked for this exact record, so the enabled, heard, bad and style rules
 * don't apply. Undefined means "not in the current crate": the player still plays it by video ID.
 */
export function findCandidate(
  shards: readonly CrateShard[],
  channels: readonly CrateChannel[],
  styleKeywords: readonly string[],
  videoId: string,
): Candidate | undefined {
  const byId = new Map(channels.map((c) => [c.id, c]));
  for (const shard of shards) {
    const record = shard.records.find((r) => r[0] === videoId);
    const found = record && toCandidate(shard, record, byId, styleKeywords);
    if (found) return found;
  }
  return undefined;
}

/** 4. Pick: random, weighted. `rng` returns [0, 1). */
export function pickWeighted(pool: readonly Candidate[], rng: () => number): Candidate | undefined {
  const total = pool.reduce((sum, c) => sum + c.weight, 0);
  if (total <= 0) return undefined;
  let r = rng() * total;
  for (const c of pool) {
    r -= c.weight;
    if (r < 0) return c;
  }
  return pool[pool.length - 1];
}

/**
 * 4b. Two-stage pick (the dig uses this): first a channel, weighted by its
 * channel weight (1, 2, 4, 8), then a record inside it, x2 for a keyword match.
 * A channel's share doesn't depend on how many records it has, so a big
 * back catalogue can't swamp a small, well-chosen one. The pool is already
 * filtered (90-day exclusion, Rediscover, bad), so a channel with nothing
 * left to play isn't in it and is never picked. `recent` (the channels of the last
 * digs, newest first) are skipped: the last 3 while 4 or more channels have eligible
 * records, otherwise only the previous dig's, and never when it is the only one left.
 * `pinned` is always picked while it has eligible records.
 */
export const REPEAT_WINDOW = 3;

export function pickTwoStage(
  pool: readonly Candidate[],
  rng: () => number,
  recent: readonly string[] | string | undefined = [],
  pinned?: string,
): Candidate | undefined {
  const last = typeof recent === 'string' ? [recent] : recent;
  const byChannel = new Map<string, { weight: number; records: Candidate[] }>();
  for (const c of pool) {
    const group = byChannel.get(c.channelId) ?? { weight: c.weight / (c.matched.length > 0 ? KEYWORD_BOOST : 1), records: [] };
    group.records.push(c);
    byChannel.set(c.channelId, group);
  }
  const all = [...byChannel.entries()];
  // Repeat window (v1.1): the last 3 digs' channels while 4 or more channels have records, else only the previous one;
  // a pinned channel is always picked while it has records, and the window pauses
  const pin = pinned === undefined ? undefined : all.filter(([id]) => id === pinned);
  const skip = new Set(last.slice(0, all.length >= 4 ? REPEAT_WINDOW : 1));
  const others = all.filter(([id]) => !skip.has(id));
  const groups = (pin && pin.length > 0 ? pin : others.length > 0 ? others : all).map(([, g]) => g);
  const total = groups.reduce((sum, g) => sum + g.weight, 0);
  if (total <= 0) return undefined;
  let r = rng() * total;
  let chosen = groups[groups.length - 1];
  for (const g of groups) {
    r -= g.weight;
    if (r < 0) {
      chosen = g;
      break;
    }
  }
  if (!chosen) return undefined;
  // Inside the channel, only the keyword boost counts
  return pickWeighted(chosen.records.map((c) => ({ ...c, weight: c.matched.length > 0 ? KEYWORD_BOOST : 1 })), rng);
}

/** 5. Why line: "From *Channel* · tagged soul-jazz · matched: horns, organ". */
export function whyLine(c: Candidate, channel: Pick<CrateChannel, 'genres'> | undefined): { from: string; parts: string[] } {
  // Genres mode: "From Channel · genre funk" (the style has no effect there)
  if (c.genre) return { from: c.channelName, parts: [`genre ${c.genre}`] };
  const parts: string[] = [];
  const tag = channel?.genres[0];
  if (tag) parts.push(`tagged ${tag.replace(/\s+/g, '-')}`);
  if (c.matched.length > 0) parts.push(`matched: ${c.matched.join(', ')}`);
  return { from: c.channelName, parts };
}

/** Jump (F): a random point between 10% and 80% of the record. */
export function jumpPoint(durationS: number, rng: () => number): number {
  return Math.round(durationS * (0.1 + 0.7 * rng()));
}

/**
 * Titles for reopened marks and history rows, read from the crate shards that are still allowed (under 30 days old).
 * A record that has left the crate has none: the screens say so instead of keeping a title past the 30-day rule.
 */
export function titlesFromShards(shards: readonly CrateShard[], now: number): Map<string, string> {
  const titles = new Map<string, string>();
  for (const shard of shards) {
    if (crateExpired(shard, now)) continue;
    for (const [videoId, title] of shard.records) if (!titles.has(videoId)) titles.set(videoId, title);
  }
  return titles;
}

/** More and Less on the Why line (SPEC F6): the channel's local weight steps off, 1, 2, 3, 4. Less at 1 turns the channel off; More on an off channel turns it on at 1. */
export function stepWeight(c: Pick<CrateChannel, 'weight' | 'enabled'>, dir: 'more' | 'less'): { weight: 1 | 2 | 3 | 4; enabled: boolean } {
  if (dir === 'more') {
    if (!c.enabled) return { weight: 1, enabled: true };
    return { weight: Math.min(4, c.weight + 1) as 1 | 2 | 3 | 4, enabled: true };
  }
  if (!c.enabled) return { weight: c.weight, enabled: false };
  if (c.weight === 1) return { weight: 1, enabled: false };
  return { weight: (c.weight - 1) as 1 | 2 | 3 | 4, enabled: true };
}
